import { useEffect, useState } from 'react';
import { api, isApiError } from '@/lib/api';

export type PatientPhotoState =
  | { status: 'loading' | 'none' }
  | { status: 'ready'; url: string }
  | { status: 'error'; message: string; requestId?: string };

/**
 * RL-1157: the profile photo the patient set in the app, read with the
 * session (GET /patients/:id/photo: patients:read, audited, `no-store`) into
 * an object URL that is revoked when the photo changes or the view unmounts.
 * `photoKey` (the photo's id and updatedAt) refetches a replaced photo; null =
 * no photo on the record (or no permission), so nothing is requested. One
 * place for the fetch: when images move to S3 (RL-1178) the endpoint stays.
 */
export function usePatientPhoto(
  patientId: string,
  photoKey: string | null,
): PatientPhotoState & { retry: () => void } {
  const [attempt, setAttempt] = useState(0);
  const key = photoKey ? `${patientId}|${photoKey}|${attempt}` : null;
  const [result, setResult] = useState<{
    key: string;
    state: PatientPhotoState;
  } | null>(null);

  useEffect(() => {
    if (!key) return;
    const controller = new AbortController();
    let url: string | null = null;
    api
      .get<Blob>(`/patients/${patientId}/photo`, {
        responseType: 'blob',
        cache: 'no-store',
        headers: { Accept: 'image/jpeg, image/png, application/problem+json' },
        signal: controller.signal,
      })
      .then(({ data }) => {
        if (controller.signal.aborted) return;
        url = URL.createObjectURL(data);
        setResult({ key, state: { status: 'ready', url } });
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        setResult({
          key,
          state:
            isApiError(err) && err.code === 'PATIENT_PHOTO_NOT_FOUND'
              ? { status: 'none' }
              : {
                  status: 'error',
                  message: isApiError(err)
                    ? err.message
                    : 'Something went wrong.',
                  requestId: isApiError(err) ? err.requestId : undefined,
                },
        });
      });
    return () => {
      controller.abort();
      if (url) URL.revokeObjectURL(url);
    };
  }, [patientId, key]);

  const retry = () => setAttempt((n) => n + 1);
  if (!key) return { status: 'none', retry };
  if (result?.key !== key) return { status: 'loading', retry };
  return { ...result.state, retry };
}
